import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Tag, X, Trash2, GitMerge, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const STAGES = ['prospect', 'cultivating', 'solicited', 'donor', 'steward', 'lapsed'];

/**
 * Floating action bar shown when 1+ donor is selected in the donor list.
 * Supports bulk tag add/remove, stage change, appended note, review clearing,
 * bulk delete, and (when 2+ selected) initiating a merge.
 */
export function BulkActionBar({ api, selectedIds, onClearSelection, onOpenMerge, onDone, allSelected, onToggleAll, totalVisible }) {
  const [action, setAction] = useState(null);   // 'tag' | 'stage' | 'note' | null
  const [tagInput, setTagInput] = useState('');
  const [stage, setStage] = useState('donor');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const count = selectedIds.size;
  if (count === 0) return null;

  const applyTag = async (mode) => {
    if (!tagInput.trim()) { toast.error('Enter a tag'); return; }
    setBusy(true);
    try {
      const body = { donor_ids: [...selectedIds] };
      if (mode === 'add') body.tags_add = tagInput.split(',').map(t => t.trim()).filter(Boolean);
      else body.tags_remove = tagInput.split(',').map(t => t.trim()).filter(Boolean);
      await api.post('/donor-bulk-update', body);
      toast.success(`${mode === 'add' ? 'Added' : 'Removed'} tag on ${count} donor${count === 1 ? '' : 's'}`);
      setTagInput(''); setAction(null); onDone();
    } catch { toast.error('Bulk update failed'); }
    finally { setBusy(false); }
  };

  const applyStage = async () => {
    setBusy(true);
    try {
      await api.post('/donor-bulk-update', { donor_ids: [...selectedIds], pipeline_stage: stage });
      toast.success(`Moved ${count} donor${count === 1 ? '' : 's'} to ${stage}`);
      setAction(null); onDone();
    } catch { toast.error('Bulk update failed'); }
    finally { setBusy(false); }
  };

  const applyNote = async () => {
    if (!note.trim()) { toast.error('Enter a note'); return; }
    setBusy(true);
    try {
      await api.post('/donor-bulk-update', { donor_ids: [...selectedIds], notes_append: note });
      toast.success(`Appended note to ${count} donor${count === 1 ? '' : 's'}`);
      setNote(''); setAction(null); onDone();
    } catch { toast.error('Bulk update failed'); }
    finally { setBusy(false); }
  };

  const clearReview = async () => {
    setBusy(true);
    try {
      await api.post('/donor-bulk-update', { donor_ids: [...selectedIds], clear_review: true });
      toast.success(`Cleared review flag on ${count}`);
      onDone();
    } catch { toast.error('Failed'); }
    finally { setBusy(false); }
  };

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${count} donor${count === 1 ? '' : 's'} and their gifts? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const res = await api.post('/donor-bulk-delete', { donor_ids: [...selectedIds] });
      toast.success(`Deleted ${res.data.deleted}`);
      onClearSelection(); onDone();
    } catch { toast.error('Bulk delete failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="sticky top-14 z-20 bg-white border-2 border-[#6B3FA0] rounded-lg shadow-lg p-3 mb-3 flex items-center gap-2 flex-wrap"
         data-testid="bulk-action-bar">
      <label className="flex items-center gap-2 text-sm cursor-pointer select-none pr-2 border-r">
        <input type="checkbox" checked={allSelected} onChange={onToggleAll} data-testid="select-all-toggle" />
        <span className="font-semibold text-[#6B3FA0]">{count} selected</span>
        {totalVisible > count && <span className="text-xs text-gray-500">of {totalVisible}</span>}
      </label>

      <Button variant="outline" size="sm" onClick={() => setAction(a => a === 'tag' ? null : 'tag')}
              data-testid="bulk-tag-btn">
        <Tag className="h-4 w-4 mr-1" /> Tag
      </Button>
      <Button variant="outline" size="sm" onClick={() => setAction(a => a === 'stage' ? null : 'stage')}
              data-testid="bulk-stage-btn">Change stage</Button>
      <Button variant="outline" size="sm" onClick={() => setAction(a => a === 'note' ? null : 'note')}
              data-testid="bulk-note-btn">Append note</Button>
      <Button variant="outline" size="sm" onClick={clearReview} disabled={busy}
              data-testid="bulk-clear-review-btn">
        <CheckCircle2 className="h-4 w-4 mr-1" /> Clear review flag
      </Button>
      <Button variant="outline" size="sm" onClick={onOpenMerge} disabled={count < 2}
              title={count < 2 ? 'Select 2+ donors to merge' : 'Merge selected into one'}
              data-testid="bulk-merge-btn">
        <GitMerge className="h-4 w-4 mr-1" /> Merge…
      </Button>
      <Button variant="outline" size="sm" onClick={bulkDelete} disabled={busy}
              className="text-red-600 border-red-300 hover:bg-red-50" data-testid="bulk-delete-btn">
        <Trash2 className="h-4 w-4 mr-1" /> Delete
      </Button>
      <Button variant="ghost" size="sm" onClick={onClearSelection} data-testid="bulk-clear-selection">
        <X className="h-4 w-4" />
      </Button>

      {action === 'tag' && (
        <div className="w-full flex items-center gap-2 mt-2 pt-2 border-t" data-testid="bulk-tag-panel">
          <Input value={tagInput} onChange={e => setTagInput(e.target.value)} placeholder="major-gift, 2025-appeal"
                 className="flex-1" data-testid="bulk-tag-input" />
          <Button size="sm" onClick={() => applyTag('add')} disabled={busy} data-testid="bulk-tag-add">Add</Button>
          <Button size="sm" variant="outline" onClick={() => applyTag('remove')} disabled={busy} data-testid="bulk-tag-remove">Remove</Button>
        </div>
      )}
      {action === 'stage' && (
        <div className="w-full flex items-center gap-2 mt-2 pt-2 border-t" data-testid="bulk-stage-panel">
          <Label className="text-sm">Move to</Label>
          <select value={stage} onChange={e => setStage(e.target.value)}
                  className="border rounded px-3 py-1.5 text-sm capitalize" data-testid="bulk-stage-select">
            {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <Button size="sm" onClick={applyStage} disabled={busy} data-testid="bulk-stage-apply">Apply</Button>
        </div>
      )}
      {action === 'note' && (
        <div className="w-full flex items-center gap-2 mt-2 pt-2 border-t" data-testid="bulk-note-panel">
          <Input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g., Mailed 2026 appeal"
                 className="flex-1" data-testid="bulk-note-input" />
          <Button size="sm" onClick={applyNote} disabled={busy} data-testid="bulk-note-apply">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Append'}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Merge dialog — pick which selected donor is the primary, review the "winner"
 * fields (name/email/phone/org/address), and confirm. Backend picks non-empty
 * winners automatically for anything not overridden here.
 */
export function MergeDialog({ open, onClose, api, donors, onMerged }) {
  const [primaryId, setPrimaryId] = useState(donors[0]?.id || '');
  const [overrides, setOverrides] = useState({});
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (open) {
      setPrimaryId(donors[0]?.id || '');
      setOverrides({});
    }
  }, [open, donors]);

  if (!open) return null;
  const primary = donors.find(d => d.id === primaryId) || donors[0];
  const dupIds = donors.filter(d => d.id !== primaryId).map(d => d.id);

  const preview = (field) => {
    if (overrides[field] != null) return overrides[field];
    const primaryVal = (primary?.[field] || '').trim();
    if (primaryVal && primaryVal !== 'TO FILL') return primaryVal;
    for (const d of donors) {
      if (d.id === primaryId) continue;
      const v = (d[field] || '').trim();
      if (v && v !== 'TO FILL') return v;
    }
    return '';
  };

  const doMerge = async () => {
    if (!primaryId || dupIds.length === 0) return;
    setBusy(true);
    try {
      await api.post('/donor-merge', {
        primary_id: primaryId,
        duplicate_ids: dupIds,
        field_overrides: Object.fromEntries(Object.entries(overrides).filter(([, v]) => v)),
      });
      toast.success(`Merged ${dupIds.length + 1} records into 1`);
      onMerged();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Merge failed');
    } finally { setBusy(false); }
  };

  const FIELDS = [
    ['name', 'Name'], ['org', 'Organization'], ['email', 'Email'],
    ['phone', 'Phone'], ['address', 'Address'],
  ];

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="merge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-5 w-5 text-[#6B3FA0]" /> Merge {donors.length} donors into one
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="mb-2 block">Primary record (winner — the one kept)</Label>
            <div className="space-y-1.5">
              {donors.map(d => (
                <label key={d.id}
                       className={`flex items-start gap-2 p-2 border rounded cursor-pointer ${primaryId === d.id ? 'border-[#6B3FA0] bg-purple-50' : 'border-gray-200'}`}
                       data-testid={`merge-primary-option-${d.id}`}>
                  <input type="radio" name="primary" value={d.id}
                         checked={primaryId === d.id}
                         onChange={() => setPrimaryId(d.id)}
                         className="mt-1" />
                  <div className="flex-1 text-sm">
                    <div className="font-semibold">{d.name}{d.org ? ` — ${d.org}` : ''}</div>
                    <div className="text-xs text-gray-500">
                      {d.email || 'no email'} · {d.phone || 'no phone'} · {d.gift_count || 0} gifts · created {(d.created_at || '').slice(0, 10)}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="border-t pt-3">
            <Label className="mb-2 block">Field winners (leave blank to auto-pick first non-empty)</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {FIELDS.map(([k, label]) => (
                <div key={k}>
                  <Label className="text-xs text-gray-500">{label}</Label>
                  <div className="flex gap-1">
                    <Input value={overrides[k] ?? preview(k)}
                           onChange={e => setOverrides(o => ({ ...o, [k]: e.target.value }))}
                           placeholder="—"
                           data-testid={`merge-field-${k}`} />
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {[...new Set(donors.map(d => (d[k] || '').trim()).filter(v => v && v !== 'TO FILL'))].map(v => (
                      <button key={v} type="button"
                              onClick={() => setOverrides(o => ({ ...o, [k]: v }))}
                              className="text-[10px] px-1.5 py-0.5 border rounded hover:bg-gray-100"
                              data-testid={`merge-field-${k}-suggest`}>
                        {v.length > 30 ? v.slice(0, 28) + '…' : v}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-xs text-gray-500 border-t pt-3">
            All gifts from the {dupIds.length} duplicate record{dupIds.length === 1 ? '' : 's'} will be reassigned to the primary.
            Tags will be unioned. Notes will be concatenated. The duplicate records will be deleted.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={doMerge} disabled={busy || !primaryId || dupIds.length === 0} data-testid="merge-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Merge into "${primary?.name || '?'}"`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
